import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Form, Input } from "antd";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { merge } from "lodash";

import Modals from "../../common/modal";
import { ltrim } from "../../../util/helper";
import {
  asyncCreatePlant,
  asyncEditPlant,
  asyncGetPlant,
} from "../../../redux/slices/plantSlice";
import { PlantFormSchema } from "../../../validators/formSchema";
import { asyncSearchAndGetTerminal } from "../../../redux/slices/terminalSlice";

// const { Option } = Select;

const PlantModal = ({ openModal, setOpenModal, messageApi, modalData }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const { loading, pagination, editLoading } = useSelector((state) => ({
    loading: state.plant.createPlant.loading,
    pagination: state.plant.getPlant.pagination,
    editLoading: state.plant.editPlant.loading,
    terminalData: state.terminal.searchAndGetTerminal.data,
  }));

  const [isDirty, setIsDirty] = useState(false);
  const [prevLat, setPrevLat] = useState("");
  const [prevLong, setPrevLong] = useState("");
  const [isLatLongChanged, setIsLatLongChanged] = useState(false);

  useEffect(() => {
    if (modalData?.data) {
      setPrevLat(modalData.data?.latitude);
      setPrevLong(modalData.data?.longitude);
    }
  }, [modalData]);

  const handleLatLongChange = (values) => {
    const isLatChanged = values.latitude !== prevLat;
    const isLongChanged = values.longitude !== prevLong;
    setIsLatLongChanged(isLatChanged || isLongChanged);
  };

  const footerElem = [
    <div className="modal_footer">
      <Button
        key="submit"
        loading={modalData.mode === "add" ? loading : editLoading}
        disabled={modalData.mode === "edit" && !isDirty}
        onClick={() => {
          if (formRef.current) {
            formRef.current.handleSubmit();
          }
        }}
      >
        {modalData.mode === "add" ? "Submit" : "Update"}
      </Button>
      <Button key="back" onClick={() => setOpenModal(false)}>
        Cancel
      </Button>
    </div>,
  ];

  const initialValue = {
    plantName: "",
    code: "",
    state: "",
    city: "",
    latitude: "",
    longitude: "",
    // terminal_id: "",
  };

  useLayoutEffect(() => {
    dispatch(asyncSearchAndGetTerminal());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modals
      title={modalData.mode === "edit" ? "Update Plant" : "Create New Plant"}
      open={openModal}
      setOpen={setOpenModal}
      footer={footerElem}
      width={700}
    >
      <Formik
        innerRef={formRef}
        validationSchema={PlantFormSchema}
        initialValues={
          modalData?.mode
            ? merge(initialValue, { ...modalData.data })
            : initialValue
        }
        onSubmit={(values) => {
          const trimValue = {
            plantName: values.plantName?.trimEnd() || "",
            code: values.code?.trimEnd() || "",
            state: values.state?.trimEnd() || "",
            city: values.city?.trimEnd() || "",
            latitude: values.latitude?.trimEnd() || "",
            longitude: values.longitude?.trimEnd() || "",
          };
          const newValues = {
            ...trimValue,
            id: values.id,
            isLatLongChanged: isLatLongChanged,
          };

          if (modalData.mode === "add") {
            dispatch(asyncCreatePlant(trimValue)).then((res) => {
              if (res.payload?.status === 201) {
                messageApi.open({
                  type: "success",
                  content:
                    res.payload?.data?.message || "Plant Created Successfully",
                });
                dispatch(
                  asyncGetPlant({
                    page: pagination.page,
                    limit: pagination.pageSize,
                  })
                );
                setOpenModal(false);
              } else {
                messageApi.open({
                  type: "error",
                  content: res.payload?.message || "Something went wrong",
                });
              }
            });
          } else {
            dispatch(asyncEditPlant(newValues)).then((res) => {
              if (res.payload?.status === 200) {
                messageApi.open({
                  type: "success",
                  content:
                    res.payload?.data?.message || "Plant Updated Successfully",
                });
                dispatch(
                  asyncGetPlant({
                    page: pagination.page,
                    limit: pagination.pageSize,
                  })
                );
                setOpenModal(false);
              } else {
                messageApi.open({
                  type: "error",
                  content: res.payload?.message || "Something went wrong",
                });
              }
            });
          }
        }}
      >
        {({
          handleChange,
          handleSubmit,
          values,
          errors,
          touched,
          setDirty,
        }) => (
          <div className="custom-scrollbar form_div">
            {/* <Divider style={{ margin: "10px 0" }} /> */}
            <Form
              onFinish={(e) => {
                setDirty(false);
                handleSubmit(e);
              }}
            >
              <div className="form_flex_two">
                <div className="form_flex_two_left">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="name1" className="form-label required">
                      Plant name
                    </label>
                    <Input
                      id="name1"
                      type="text"
                      name="plantName"
                      className="form-input"
                      placeholder="Enter plant name"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.plantName ? ltrim(values?.plantName) : ""}
                      status={`${
                        (errors.plantName && touched.plantName && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.plantName && touched.plantName && (
                    <small id="plantName-help" className="error-message">
                      {errors.plantName}
                    </small>
                  )}
                </div>
                <div className="form_flex_two_right">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="code1" className="form-label required">
                      Code
                    </label>
                    <Input
                      id="code1"
                      type="text"
                      name="code"
                      className="form-input"
                      placeholder="Enter code"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.code ? ltrim(values?.code) : ""}
                      status={`${
                        (errors.code && touched.code && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.code && touched.code && (
                    <small id="code-help" className="error-message">
                      {errors.code}
                    </small>
                  )}
                </div>
              </div>
              <div className="form_flex_two">
                <div className="form_flex_two_left">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="city1" className="form-label required">
                      City
                    </label>
                    <Input
                      id="city1"
                      type="text"
                      name="city"
                      className="form-input"
                      placeholder="Enter city"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.city ? ltrim(values?.city) : ""}
                      status={`${
                        (errors.city && touched.city && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.city && touched.city && (
                    <small id="city-help" className="error-message">
                      {errors.city}
                    </small>
                  )}
                </div>
                <div className="form_flex_two_right">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="state1" className="form-label required">
                      State
                    </label>
                    <Input
                      id="state1"
                      type="text"
                      name="state"
                      className="form-input"
                      placeholder="Enter state"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                        }
                      }}
                      value={values?.state ? ltrim(values?.state) : ""}
                      status={`${
                        (errors.state && touched.state && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.state && touched.state && (
                    <small id="state-help" className="error-message">
                      {errors.state}
                    </small>
                  )}
                </div>
              </div>
              <div className="form_flex_two">
                <div className="form_flex_two_left">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="latitude1" className="form-label required">
                      Latitude
                    </label>
                    <Input
                      id="latitude1"
                      type="text"
                      name="latitude"
                      className="form-input"
                      placeholder="Enter latitude"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                          handleLatLongChange({
                            ...values,
                            latitude: e.target.value,
                          });
                        }
                      }}
                      value={values?.latitude ? ltrim(values?.latitude) : ""}
                      status={`${
                        (errors.latitude && touched.latitude && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.latitude && touched.latitude && (
                    <small id="latitude-help" className="error-message">
                      {errors.latitude}
                    </small>
                  )}
                </div>
                <div className="form_flex_two_right">
                  <Form.Item style={{ margin: "0" }}>
                    <label htmlFor="longitude1" className="form-label required">
                      Longitude
                    </label>
                    <Input
                      id="longitude1"
                      type="text"
                      name="longitude"
                      className="form-input"
                      placeholder="Enter longitude"
                      onChange={(e) => {
                        if (handleChange) {
                          handleChange(e);
                          setIsDirty(true);
                          handleLatLongChange({
                            ...values,
                            longitude: e.target.value,
                          });
                        }
                      }}
                      value={values?.longitude ? ltrim(values?.longitude) : ""}
                      status={`${
                        (errors.longitude && touched.longitude && "error") || ""
                      }`}
                    />
                  </Form.Item>
                  {errors.longitude && touched.longitude && (
                    <small id="longitude-help" className="error-message">
                      {errors.longitude}
                    </small>
                  )}
                </div>
              </div>
              {/* <Form.Item style={{ margin: "0" }}>
                <label htmlFor="terminal1" className="form-label required">
                  Terminal
                </label>
                <Select
                  id="terminal1"
                  style={{ height: "2.8rem" }}
                  placeholder="Select a terminal"
                  onChange={(value) => setFieldValue("terminal_id", value)}
                  showSearch
                  allowClear
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option?.children
                      ?.toLowerCase()
                      ?.indexOf(input?.toLowerCase()) >= 0
                  }
                  status={`${
                    (errors.terminal_id && touched.terminal_id && "error") || ""
                  }`}
                  value={values.terminal_id}
                >
                  {terminalData?.map((option) => (
                    <Option key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {errors.terminal_id && touched.terminal_id && (
                <small id="terminal_id-help" className="error-message">
                  {errors.terminal_id}
                </small>
              )} */}
            </Form>
          </div>
        )}
      </Formik>
    </Modals>
  );
};

export default PlantModal;
